import React, { createContext, useContext, useReducer } from 'react';
import PropTypes from 'prop-types';
import useLogRocketSession from '@afosto/utils-react/authentication/useLogRocketSession';
import useServiceWorker from './useServiceWorker';
import useSplashScreen from '@afosto/utils-react/authentication/useSplashScreen';
import useTenantSwitched from '@afosto/utils-react/authentication/useTenantSwitched';
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import { addLocaleData, IntlProvider } from 'react-intl';
import nl from 'react-intl/locale-data/nl';
import en from 'react-intl/locale-data/en';
import { ThemeProvider } from 'styled-components';
import { SnackbarProvider } from 'notistack';
import { createMuiTheme } from '@material-ui/core';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ConfirmationServiceProvider } from '../ConfirmDialog';
import createAppReducer, { createAppInitialState } from './reducer';
import createAppActions from './createAppActions';
import ApplicationUpdateNotification from '../ApplicationUpdateNotification';
import SessionErrorDialog from '../SessionErrorDialog';
import SplashScreen from '../SplashScreen';
import SwitchedTenantDialog from '../SwitchedTenantDialog';
import AfostoTheme from '@afosto/components-react/theme';
import IntlContextProvider from '@afosto/utils-react/intl/IntlContextProvider';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import flattenObject from '@afosto/utils/object/flattenObject';
import apps from '@afosto/utils-react/subscription/apps';
import useSessionError from '@afosto/auth-react/useSessionError';

export const AppContext = createContext({});
export const AppActionsContext = createContext([]);

export const useAppState = () => useContext(AppContext);
export const useAppActions = () => useContext(AppActionsContext);

addLocaleData([...nl, ...en]);

if (!process.env.NODE_ENV || process.env.NODE_ENV !== 'development') {
  LogRocket.init(process.env.REACT_APP_LOGROCKET_APP_ID, {
    rootHostname: process.env.REACT_APP_LOGROCKET_ROOT_HOSTNAME,
  });
  setupLogRocketReact(LogRocket);
}

const AppProvider = ({
  children,
  state,
  app: appKey,
  reducers,
  actions,
  defaultLocale,
  locale,
  messages,
  snackbarProps,
  theme,
}) => {
  useLogRocketSession();

  const showSplashScreen = useSplashScreen();
  const tenantSwitched = useTenantSwitched();
  const [swUpdateAvailable, updateServiceWorker] = useServiceWorker();
  const [hasSessionError, sessionError] = useSessionError();
  const [appState, dispatch] = useReducer(
    createAppReducer(reducers),
    createAppInitialState({
      appKey,
      defaultLocale,
      locale,
      ...state,
    }),
  );
  const appActions = createAppActions(dispatch, actions);
  const currentApp = findObjectInArray(apps, 'key', appKey);
  const localeMessages = messages[appState.locale] || messages[appState.defaultLocale];

  return (
    <AppActionsContext.Provider value={[appActions, dispatch]}>
      <AppContext.Provider value={appState}>
        <IntlProvider
          locale={locale}
          defaultLocale={defaultLocale}
          messages={flattenObject(localeMessages)}
        >
          <IntlContextProvider>
            <MuiThemeProvider theme={theme}>
              <CssBaseline />
              <ThemeProvider theme={theme}>
                <>
                  <SnackbarProvider maxSnack={3} autoHideDuration={3000} {...snackbarProps}>
                    <ConfirmationServiceProvider>
                      {showSplashScreen && !hasSessionError && (
                        <SplashScreen icon={currentApp?.icon} />
                      )}
                      {!showSplashScreen && children}
                    </ConfirmationServiceProvider>
                  </SnackbarProvider>
                  {tenantSwitched && <SwitchedTenantDialog />}
                  <SessionErrorDialog isOpen={hasSessionError} error={sessionError} />
                  <ApplicationUpdateNotification
                    isOpen={swUpdateAvailable}
                    onClick={updateServiceWorker}
                  />
                </>
              </ThemeProvider>
            </MuiThemeProvider>
          </IntlContextProvider>
        </IntlProvider>
      </AppContext.Provider>
    </AppActionsContext.Provider>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node,
  state: PropTypes.object,
  reducers: PropTypes.object,
  actions: PropTypes.object,
  app: PropTypes.string,
  defaultLocale: PropTypes.string,
  locale: PropTypes.string,
  messages: PropTypes.object,
  snackbarProps: PropTypes.object,
  theme: PropTypes.object,
};

AppProvider.defaultProps = {
  children: undefined,
  state: {},
  reducers: {},
  actions: {},
  app: null,
  defaultLocale: 'nl',
  locale: navigator.language,
  messages: {},
  theme: createMuiTheme(AfostoTheme),
  snackbarProps: {},
};

export default AppProvider;
